/**
 * WMN catalog entity graph — lazy-loaded side-car for engagement / discover.
 * Cuba/country query aliases + linkQuery (v2026-08-04b).
 *
 * Exposes window.wmnCatalogGraph:
 *   ensureLoaded() → Promise<data>
 *   resolveProduct(handle) → { artists, genres, imprints }
 *   expand(entities, depth?) → candidate product handles
 *   relatedGenres(slug, kinds?) → [{ slug, kind }]  kind ∈ p|c|s
 *   linkQuery(text) → [{ id, score }]
 */
(function (window, document) {
  'use strict';

  /* v8: invalidate stale session graphs (e.g. Hlybini latin-america) that survived hard refresh */
  var CACHE_KEY = 'wmn_catalog_graph_v9';
  var ACCEPTED_GRAPH_V = { 1: 1, 2: 1, 3: 1, 4: 1, 5: 1 };
  var data = null;
  var loadPromise = null;
  var scriptEl =
    document.currentScript ||
    document.querySelector('script[data-wmn-catalog-graph]');

  function graphUrl() {
    if (scriptEl) {
      var src = scriptEl.getAttribute('data-src');
      if (src) return src;
    }
    var meta = document.querySelector('meta[name="wmn-catalog-graph-url"]');
    if (meta) {
      var content = meta.getAttribute('content');
      if (content) return content;
    }
    return null;
  }

  function normalize(str) {
    var s = String(str || '').toLowerCase();
    try {
      s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    } catch (e) {
      /* older browsers */
    }
    return s
      .replace(/['']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\s+/g, ' ');
  }

  function readSessionCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      var url = graphUrl();
      /* Envelope { url, graph } — drop cache when asset URL / cachebust changes */
      if (parsed && parsed.graph && parsed.graph.products) {
        if (url && parsed.url && parsed.url !== url) return null;
        if (ACCEPTED_GRAPH_V[parsed.graph.v]) return parsed.graph;
        return null;
      }
      /* Legacy bare graph blob */
      if (parsed && parsed.products && ACCEPTED_GRAPH_V[parsed.v]) {
        return parsed;
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function writeSessionCache(graph) {
    try {
      window.sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ url: graphUrl(), graph: graph })
      );
    } catch (e) {
      /* quota / private mode */
    }
  }

  /** Seed imageCache from baked graph `img` fields — For you must not wait on product.js. */
  function seedImageCacheFromGraph(graph) {
    if (!graph || !graph.products) return;
    var handles = Object.keys(graph.products);
    var i;
    for (i = 0; i < handles.length; i++) {
      var h = handles[i];
      var p = graph.products[h];
      if (!p || !p.img) continue;
      if (Object.prototype.hasOwnProperty.call(imageCache, h) && imageCache[h]) {
        continue;
      }
      imageCache[h] = withImageWidth(normalizeImageUrl(p.img), 200) || p.img;
    }
  }

  function fetchGraph() {
    var url = graphUrl();
    if (!url) {
      return Promise.reject(new Error('wmn-catalog-graph: missing data-src'));
    }
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('wmn-catalog-graph: HTTP ' + res.status);
      return res.json();
    });
  }

  function handleize(text) {
    return normalize(text).replace(/\s+/g, '-').replace(/^-|-$/g, '').slice(0, 100);
  }

  function shopRoot() {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    if (root.slice(-1) !== '/') root += '/';
    return root;
  }

  function genreSlugFromTag(tag, genres) {
    if (!tag || !genres) return '';
    var needle = String(tag).toLowerCase();
    var slugs = Object.keys(genres);
    var i;
    for (i = 0; i < slugs.length; i++) {
      var slug = slugs[i];
      var node = genres[slug];
      var label = node && node.n ? String(node.n).toLowerCase() : '';
      if (label && label === needle) return slug;
      if (slug === handleize(tag)) return slug;
    }
    return '';
  }

  function productTags(raw) {
    if (!raw) return [];
    if (Object.prototype.toString.call(raw) === '[object Array]') {
      return raw.map(function (t) {
        return String(t || '').trim();
      }).filter(Boolean);
    }
    return String(raw)
      .split(',')
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
  }

  function coverFromStorefrontProduct(p) {
    if (!p) return '';
    if (p.featured_image) {
      if (typeof p.featured_image === 'string') return p.featured_image;
      if (p.featured_image.src) return p.featured_image.src;
    }
    if (p.images && p.images.length) {
      var img = p.images[0];
      if (typeof img === 'string') return img;
      if (img && img.src) return img.src;
    }
    return '';
  }

  /** Merge published products missing from the baked graph (same-day releases). */
  function mergeStorefrontProduct(graph, p) {
    if (!graph || !graph.products || !p || !p.handle) return false;
    var handle = String(p.handle);
    if (graph.products[handle]) return false;

    var type = String(p.product_type || '').trim();
    var artistSlug = type ? handleize(type) : '';
    var vendor = String(p.vendor || '').trim();
    var imprintSlug = vendor ? handleize(vendor) : '';
    var tags = productTags(p.tags);
    var genreSlugs = [];
    var ti;
    for (ti = 0; ti < tags.length; ti++) {
      var gs = genreSlugFromTag(tags[ti], graph.genres);
      if (gs && genreSlugs.indexOf(gs) === -1) genreSlugs.push(gs);
    }

    var node = {
      t: String(p.title || handle),
      g: genreSlugs
    };
    if (artistSlug) node.a = artistSlug;
    if (imprintSlug) node.i = imprintSlug;
    var img = coverFromStorefrontProduct(p);
    if (img) node.img = img;
    graph.products[handle] = node;

    if (artistSlug) {
      var artist = graph.artists[artistSlug] || {
        n: type,
        g: [],
        p: [],
        r: []
      };
      if (artist.p.indexOf(handle) === -1) artist.p.push(handle);
      var gi;
      for (gi = 0; gi < genreSlugs.length; gi++) {
        if (artist.g.indexOf(genreSlugs[gi]) === -1) artist.g.push(genreSlugs[gi]);
      }
      graph.artists[artistSlug] = artist;
    }

    for (gi = 0; gi < genreSlugs.length; gi++) {
      var gnode = graph.genres[genreSlugs[gi]];
      if (!gnode) continue;
      if (!gnode.p) gnode.p = [];
      if (gnode.p.indexOf(handle) === -1) gnode.p.push(handle);
      if (artistSlug) {
        if (!gnode.a) gnode.a = [];
        if (gnode.a.indexOf(artistSlug) === -1) gnode.a.push(artistSlug);
      }
    }

    if (imprintSlug) {
      var imprint = graph.imprints[imprintSlug] || { n: vendor, p: [] };
      if (imprint.p.indexOf(handle) === -1) imprint.p.push(handle);
      graph.imprints[imprintSlug] = imprint;
    }

    if (img) {
      imageCache[handle] = withImageWidth(normalizeImageUrl(img), 200) || img;
    }
    return true;
  }

  var liveSyncPromise = null;

  function syncLiveProducts() {
    if (!data || !data.products) return Promise.resolve(data);
    if (liveSyncPromise) return liveSyncPromise;

    liveSyncPromise = (function () {
      var page = 1;
      var added = 0;
      var root = shopRoot();

      function next() {
        return fetch(
          root + 'products.json?limit=250&page=' + page,
          { credentials: 'same-origin' }
        )
          .then(function (res) {
            if (!res.ok) throw new Error('products.json ' + res.status);
            return res.json();
          })
          .then(function (json) {
            var list = (json && json.products) || [];
            var i;
            for (i = 0; i < list.length; i++) {
              if (mergeStorefrontProduct(data, list[i])) added += 1;
            }
            if (list.length === 250) {
              page += 1;
              return next();
            }
            return added;
          });
      }

      return next()
        .then(function (n) {
          liveSyncPromise = null;
          if (n > 0) {
            seedImageCacheFromGraph(data);
            writeSessionCache(data);
            try {
              document.dispatchEvent(
                new CustomEvent('wmn:catalog-graph-updated', {
                  detail: { added: n }
                })
              );
              document.dispatchEvent(
                new CustomEvent('wmn:focus-changed', { detail: null })
              );
            } catch (e) {
              /* ignore */
            }
          }
          return data;
        })
        .catch(function () {
          liveSyncPromise = null;
          return data;
        });
    })();

    return liveSyncPromise;
  }

  function ensureLoaded() {
    ensureAffinity();
    if (data) {
      seedImageCacheFromGraph(data);
      syncLiveProducts();
      return Promise.resolve(data);
    }
    if (loadPromise) return loadPromise;

    var cached = readSessionCache();
    if (cached) {
      data = cached;
      seedImageCacheFromGraph(data);
      syncLiveProducts();
      return Promise.resolve(data);
    }

    loadPromise = fetchGraph()
      .then(function (graph) {
        data = graph;
        seedImageCacheFromGraph(data);
        writeSessionCache(graph);
        loadPromise = null;
        syncLiveProducts();
        ensureAffinity();
        return data;
      })
      .catch(function (err) {
        loadPromise = null;
        throw err;
      });

    return loadPromise;
  }

  /**
   * @param {string} handle
   * @returns {{ artists: string[], genres: string[], countries: string[], regions: string[], imprints: string[] }}
   */
  function resolveProduct(handle) {
    var empty = {
      artists: [],
      genres: [],
      countries: [],
      regions: [],
      imprints: []
    };
    if (!data || !handle) return empty;
    var p = data.products[handle];
    if (!p) return empty;
    return {
      artists: p.a ? [p.a] : [],
      genres: p.g ? p.g.slice() : [],
      countries: p.c ? p.c.slice() : [],
      regions: p.rg ? p.rg.slice() : [],
      imprints: p.i ? [p.i] : []
    };
  }

  function entityParts(id) {
    var s = String(id || '');
    var i = s.indexOf(':');
    if (i < 0) return { kind: '', key: s };
    return { kind: s.slice(0, i), key: s.slice(i + 1) };
  }

  /**
   * Parse genres[slug].r → [{ slug, kind }] with kind ∈ p|c|s.
   * Accepts legacy plain-string lists.
   */
  function parseGenreRelated(node) {
    var out = [];
    if (!node || !node.r) return out;
    var i;
    for (i = 0; i < node.r.length; i++) {
      var row = node.r[i];
      if (!row) continue;
      if (typeof row === 'string') {
        out.push({ slug: row, kind: 's' });
      } else if (Object.prototype.toString.call(row) === '[object Array]') {
        if (row[0]) out.push({ slug: String(row[0]), kind: String(row[1] || 's') });
      }
    }
    return out;
  }

  /**
   * Related genre slugs for a genre key.
   * @param {string} slug
   * @param {string[]=} kinds optional filter e.g. ['p','c']
   * @returns {Array<{slug:string, kind:string}>}
   */
  function relatedGenres(slug, kinds) {
    if (!data || !slug || !data.genres[slug]) return [];
    var rows = parseGenreRelated(data.genres[slug]);
    if (!kinds || !kinds.length) return rows;
    var allow = {};
    var ki;
    for (ki = 0; ki < kinds.length; ki++) allow[kinds[ki]] = true;
    return rows.filter(function (r) {
      return allow[r.kind];
    });
  }

  /**
   * Walk entity IDs → related product handles.
   * entities: "artist:…", "genre:…", "country:…", "region:…", "imprint:…", "product:…"
   * Country/region walks add member products; product seeds expand their countries+regions.
   * Celtic is region-only (never country:celtic).
   */
  function expand(entities, depth) {
    if (!data) return [];
    var d = depth == null ? 1 : depth;
    var handles = {};
    var artistQueue = [];
    var genreQueue = [];
    var countryQueue = [];
    var regionQueue = [];
    var imprintQueue = [];
    var i;
    var list = entities || [];

    for (i = 0; i < list.length; i++) {
      var raw = list[i];
      if (!raw) continue;
      var parts = entityParts(raw);
      var kind = parts.kind;
      var key = parts.key;
      if (!kind) {
        if (data.products[raw]) {
          handles[raw] = true;
          continue;
        }
        if (data.artists[raw]) {
          kind = 'artist';
          key = raw;
        } else if (data.genres[raw]) {
          kind = 'genre';
          key = raw;
        } else if (data.countries && data.countries[raw]) {
          kind = 'country';
          key = raw;
        } else if (data.regions && data.regions[raw]) {
          kind = 'region';
          key = raw;
        } else if (data.imprints[raw]) {
          kind = 'imprint';
          key = raw;
        } else {
          continue;
        }
      }
      if (kind === 'product') {
        handles[key] = true;
        if (data.products[key]) {
          var prod = data.products[key];
          var pci;
          if (prod.c) {
            for (pci = 0; pci < prod.c.length; pci++) {
              if (prod.c[pci] && prod.c[pci] !== 'celtic') {
                countryQueue.push(prod.c[pci]);
              }
            }
          }
          if (prod.rg) {
            for (pci = 0; pci < prod.rg.length; pci++) {
              if (prod.rg[pci]) regionQueue.push(prod.rg[pci]);
            }
          }
        }
      } else if (kind === 'artist') {
        artistQueue.push(key);
      } else if (kind === 'genre') {
        genreQueue.push(key);
      } else if (kind === 'country') {
        if (key !== 'celtic') countryQueue.push(key);
      } else if (kind === 'region') {
        regionQueue.push(key);
      } else if (kind === 'imprint') {
        imprintQueue.push(key);
      }
    }

    function addProducts(arr) {
      if (!arr) return;
      for (var j = 0; j < arr.length; j++) handles[arr[j]] = true;
    }

    var a;
    for (i = 0; i < artistQueue.length; i++) {
      a = data.artists[artistQueue[i]];
      if (!a) continue;
      addProducts(a.p);
      // Related artists only — do NOT dump every product in the artist's
      // genres (blues+jazz on Coltrane pulled the entire Sahara/blues catalog).
      if (d >= 1 && a.r) {
        for (var r = 0; r < a.r.length; r++) {
          var related = data.artists[a.r[r]];
          if (related) addProducts(related.p);
        }
      }
    }

    var seenGenre = {};
    for (i = 0; i < genreQueue.length; i++) {
      seenGenre[genreQueue[i]] = true;
    }
    for (i = 0; i < genreQueue.length; i++) {
      var genre = data.genres[genreQueue[i]];
      if (!genre) continue;
      addProducts(genre.p);
      if (d >= 1 && genre.a) {
        for (var ai = 0; ai < genre.a.length; ai++) {
          var art = data.artists[genre.a[ai]];
          if (art) addProducts(art.p);
        }
      }
      if (d >= 1 && genre.r) {
        var rels = parseGenreRelated(genre);
        var ri;
        for (ri = 0; ri < rels.length; ri++) {
          var relSlug = rels[ri].slug;
          var relKind = rels[ri].kind;
          if (!relSlug || seenGenre[relSlug]) continue;
          var relNode = data.genres[relSlug];
          if (!relNode) continue;
          seenGenre[relSlug] = true;
          addProducts(relNode.p);
          if (d >= 2 && (relKind === 'p' || relKind === 'c') && relNode.a) {
            for (var rai = 0; rai < relNode.a.length; rai++) {
              var rart = data.artists[relNode.a[rai]];
              if (rart) addProducts(rart.p);
            }
          }
        }
      }
    }

    var seenCountry = {};
    for (i = 0; i < countryQueue.length; i++) {
      var cslug = countryQueue[i];
      if (!cslug || cslug === 'celtic' || seenCountry[cslug]) continue;
      seenCountry[cslug] = true;
      var cnode = data.countries && data.countries[cslug];
      if (cnode) addProducts(cnode.p);
    }

    var seenRegion = {};
    for (i = 0; i < regionQueue.length; i++) {
      var rslug = regionQueue[i];
      if (!rslug || seenRegion[rslug]) continue;
      seenRegion[rslug] = true;
      var rnode = data.regions && data.regions[rslug];
      if (!rnode) continue;
      addProducts(rnode.p);
      // Member countries → their products
      if (d >= 1 && rnode.c) {
        var mci;
        for (mci = 0; mci < rnode.c.length; mci++) {
          var mc = rnode.c[mci];
          if (!mc || mc === 'celtic' || seenCountry[mc]) continue;
          seenCountry[mc] = true;
          var mcnode = data.countries && data.countries[mc];
          if (mcnode) addProducts(mcnode.p);
        }
      }
    }

    for (i = 0; i < imprintQueue.length; i++) {
      var imp = data.imprints[imprintQueue[i]];
      if (imp) addProducts(imp.p);
    }

    return Object.keys(handles);
  }

  /**
   * True when query is only a given-name stub of a longer personal name
   * ("eric" → "eric clapton", "john" → "johnny cash"). Too weak to claim
   * Similar-to / affinity identity — surnames and mononyms stay OK.
   */
  function isWeakGivenNamePrefix(queryNorm, nameNorm) {
    if (!queryNorm || !nameNorm || queryNorm === nameNorm) return false;
    var qParts = queryNorm.split(' ');
    var nParts = nameNorm.split(' ');
    if (qParts.length !== 1 || nParts.length < 2) return false;
    // Exact first token, or stub of first token ("john" / "johnny").
    return (
      nParts[0] === qParts[0] || nParts[0].indexOf(qParts[0]) === 0
    );
  }

  function scoreNameMatch(queryNorm, nameNorm) {
    if (!queryNorm || !nameNorm) return 0;
    if (nameNorm === queryNorm) return 100;
    if (nameNorm.indexOf(queryNorm) === 0) {
      // "eric" prefix of "eric clapton" — keep as a weak hint only.
      if (isWeakGivenNamePrefix(queryNorm, nameNorm)) return 45;
      return 80;
    }
    if ((' ' + nameNorm).indexOf(' ' + queryNorm) >= 0) return 60;
    if (nameNorm.indexOf(queryNorm) >= 0) return 40;
    // token prefix: each query token starts a name token
    var qTokens = queryNorm.split(' ');
    var nTokens = nameNorm.split(' ');
    var hit = 0;
    for (var qi = 0; qi < qTokens.length; qi++) {
      var qt = qTokens[qi];
      for (var ni = 0; ni < nTokens.length; ni++) {
        if (nTokens[ni].indexOf(qt) === 0) {
          hit++;
          break;
        }
      }
    }
    if (hit === qTokens.length) return 50;
    if (hit > 0) return 20 + hit * 5;
    return 0;
  }

  /**
   * Country / colloquial query → genre slug (style fallback when no country node).
   * Prefer country:* entities when data.countries is present.
   * Never treat Celtic as a country.
   */
  var QUERY_ALIASES = {
    cuba: 'afro-cuban-son',
    cuban: 'afro-cuban-son',
    'afro cuban': 'afro-cuban-son',
    'afro-cuban': 'afro-cuban-son',
    'son cubano': 'afro-cuban-son',
    tuareg: 'tuareg',
    taureg: 'tuareg',
    touareg: 'tuareg',
    sahara: 'desert-blues-sahara',
    'desert blues': 'desert-blues-sahara',
    desert: 'desert-blues-sahara',
    gnawa: 'gnawa',
    morocco: 'gnawa',
    moroccan: 'gnawa',
    wedding: 'gnawa',
    'wedding music': 'gnawa',
    brazil: 'brazilian',
    brasil: 'brazilian',
    argentina: 'tango',
    jamaica: 'reggae-dancehall-ragga',
    // Short forms for compound genre slugs
    klezmer: 'klezmer-jewish',
    jewish: 'klezmer-jewish',
    fado: 'fado-portuguese',
    portuguese: 'fado-portuguese',
    reggae: 'reggae-dancehall-ragga',
    dancehall: 'reggae-dancehall-ragga',
    ragga: 'reggae-dancehall-ragga',
    bollywood: 'bollywood-filmi',
    filmi: 'bollywood-filmi',
    gypsy: 'gypsy-roma',
    roma: 'gypsy-roma',
    raga: 'indian-classical-raga'
  };

  var QUERY_COUNTRY_ALIASES = {
    mali: 'mali',
    malian: 'mali',
    bamako: 'mali',
    cuba: 'cuba',
    cuban: 'cuba',
    brazil: 'brazil',
    brasil: 'brazil',
    brazilian: 'brazil',
    jamaica: 'jamaica',
    jamaican: 'jamaica',
    usa: 'usa',
    america: 'usa',
    american: 'usa',
    mississippi: 'usa',
    china: 'china',
    japan: 'japan',
    japanese: 'japan',
    india: 'india',
    indian: 'india',
    scotland: 'scotland',
    scottish: 'scotland',
    ireland: 'ireland',
    irish: 'ireland',
    wales: 'wales',
    welsh: 'wales',
    england: 'england',
    english: 'england',
    peru: 'peru',
    senegal: 'senegal',
    ghana: 'ghana',
    nigeria: 'nigeria',
    ethiopia: 'ethiopia',
    morocco: 'morocco',
    egypt: 'egypt',
    france: 'france',
    paris: 'france',
    italy: 'italy',
    spain: 'spain',
    portugal: 'portugal',
    greece: 'greece',
    turkey: 'turkey',
    palestine: 'palestine',
    afghanistan: 'afghanistan',
    cambodia: 'cambodia',
    madagascar: 'madagascar',
    hungary: 'hungary',
    argentina: 'argentina'
  };

  /**
   * Fuzzy/prefix link of free text → entity ids with scores.
   * @returns {Array<{id: string, score: number}>}
   */
  /**
   * Exact genre query → slug (e.g. "Jazz" / "jazz" → "jazz").
   * Only exact slug or exact display-name matches — no substring aliases
   * ("india" must not become indian-classical-raga).
   * @returns {string|null}
   */
  function exactGenreFromQuery(text) {
    if (!data || !data.genres) return null;
    var q = normalize(text);
    if (!q || q.length < 2) return null;
    if (data.genres[q]) return q;
    // Colloquial / spelling aliases (Taureg → tuareg).
    var aliasSlug = QUERY_ALIASES[q];
    if (aliasSlug && data.genres[aliasSlug]) return aliasSlug;
    var slug;
    for (slug in data.genres) {
      if (!Object.prototype.hasOwnProperty.call(data.genres, slug)) continue;
      var node = data.genres[slug];
      var name = normalize((node && node.n) || '');
      if (name && name === q) return slug;
    }
    // Common short forms that equal the controlled label's distinctive token.
    if (q === 'rare groove' && data.genres['psychedelic-rare-groove']) {
      return 'psychedelic-rare-groove';
    }
    if (q === 'desert blues' && data.genres['desert-blues-sahara']) {
      return 'desert-blues-sahara';
    }
    if (q === 'indian classical' && data.genres['indian-classical-raga']) {
      return 'indian-classical-raga';
    }
    // Distinctive first token of compound slug: "klezmer" → klezmer-jewish.
    // Only when exactly one genre owns that first token (avoid "world").
    if (q.length >= 4 && q.indexOf(' ') === -1) {
      var tokenHits = [];
      for (slug in data.genres) {
        if (!Object.prototype.hasOwnProperty.call(data.genres, slug)) continue;
        if (slug === q || slug.split('-')[0] === q) tokenHits.push(slug);
      }
      if (tokenHits.length === 1) return tokenHits[0];
      if (tokenHits.length > 1) {
        // Prefer exact slug match already handled; else longest slug
        // with most products as weak tie-break.
        tokenHits.sort(function (a, b) {
          var pa = (data.genres[a].p || []).length;
          var pb = (data.genres[b].p || []).length;
          return pb - pa || b.length - a.length;
        });
        // Ambiguous head token ("world") — do not guess.
        if (tokenHits[0].split('-')[0] !== 'world') return tokenHits[0];
      }
    }
    // Typo tolerance: Taureg → tuareg (LCS ≥ 0.8, length ≥ 5).
    if (q.length >= 5) {
      var bestSlug = null;
      var bestRatio = 0;
      for (slug in data.genres) {
        if (!Object.prototype.hasOwnProperty.call(data.genres, slug)) continue;
        node = data.genres[slug];
        var candidates = [
          normalize(slug.replace(/-/g, ' ')),
          normalize((node && node.n) || ''),
          slug
        ];
        var ci;
        for (ci = 0; ci < candidates.length; ci++) {
          var cand = candidates[ci];
          if (!cand || cand.length < 5) continue;
          if (Math.abs(cand.length - q.length) > 3) continue;
          var ratio = affinityLcsRatio(q, cand);
          if (ratio >= 0.8 && ratio > bestRatio) {
            bestRatio = ratio;
            bestSlug = slug;
          }
        }
      }
      if (bestSlug) return bestSlug;
    }
    return null;
  }

  function linkQuery(text) {
    if (!data) return [];
    var q = normalize(text);
    if (!q || q.length < 2) return [];
    if (q === 'celtic' || q.indexOf('celtic') === 0) {
      // Handled below as region:celtic only — do not early-return empty
    }

    var scored = [];
    var slug;
    var node;
    var s;

    // Country alias first (Rough Guide country intent).
    // Emit even when the country node is sparse so style aliases / soft
    // product scans can still fire for queries like "wedding music Morocco".
    var countrySlug = QUERY_COUNTRY_ALIASES[q];
    if (countrySlug && countrySlug !== 'celtic') {
      if (data.countries && data.countries[countrySlug]) {
        scored.push({ id: 'country:' + countrySlug, score: 100 });
      } else {
        scored.push({ id: 'country:' + countrySlug, score: 92 });
      }
    }

    // Region phrases (Celtic, West Africa, Latin, …) — never country:celtic
    if (data.regions) {
      if (q === 'celtic' || q === 'celtic music') {
        if (data.regions.celtic) {
          scored.push({ id: 'region:celtic', score: 100 });
        }
      } else {
        for (slug in data.regions) {
          if (!Object.prototype.hasOwnProperty.call(data.regions, slug)) continue;
          node = data.regions[slug];
          s = Math.max(
            scoreNameMatch(q, normalize(node.n)),
            scoreNameMatch(q, normalize(slug.replace(/-/g, ' ')))
          );
          if (s > 0) scored.push({ id: 'region:' + slug, score: s * 0.98 });
        }
      }
    }

    // Exact genre alias hit (style fallback)
    var aliasSlug = QUERY_ALIASES[q];
    if (aliasSlug && data.genres && data.genres[aliasSlug]) {
      scored.push({ id: 'genre:' + aliasSlug, score: countrySlug ? 70 : 100 });
    }

    if (data.countries) {
      for (slug in data.countries) {
        if (!Object.prototype.hasOwnProperty.call(data.countries, slug)) continue;
        if (slug === 'celtic') continue;
        node = data.countries[slug];
        s = Math.max(
          scoreNameMatch(q, normalize(node.n)),
          scoreNameMatch(q, normalize(slug.replace(/-/g, ' ')))
        );
        if (s > 0) scored.push({ id: 'country:' + slug, score: s });
      }
    }

    for (slug in data.artists) {
      if (!Object.prototype.hasOwnProperty.call(data.artists, slug)) continue;
      node = data.artists[slug];
      s = Math.max(
        scoreNameMatch(q, normalize(node.n)),
        scoreNameMatch(q, normalize(slug.replace(/-/g, ' ')))
      );
      if (s > 0) scored.push({ id: 'artist:' + slug, score: s });
    }

    for (slug in data.genres) {
      if (!Object.prototype.hasOwnProperty.call(data.genres, slug)) continue;
      node = data.genres[slug];
      s = Math.max(
        scoreNameMatch(q, normalize(node.n)),
        scoreNameMatch(q, normalize(slug.replace(/-/g, ' ')))
      );
      // Genre typo: Taureg → tuareg when exact/prefix score is 0.
      if (s === 0 && q.length >= 5) {
        var gName = normalize((node && node.n) || '');
        var gSlug = normalize(slug.replace(/-/g, ' '));
        var gRatio = Math.max(
          affinityLcsRatio(q, gSlug),
          gName ? affinityLcsRatio(q, gName) : 0
        );
        if (gRatio >= 0.8) s = Math.round(gRatio * 90);
      }
      if (s > 0) scored.push({ id: 'genre:' + slug, score: s * 0.95 });
    }

    for (slug in data.imprints) {
      if (!Object.prototype.hasOwnProperty.call(data.imprints, slug)) continue;
      node = data.imprints[slug];
      s = Math.max(
        scoreNameMatch(q, normalize(node.n)),
        scoreNameMatch(q, normalize(slug.replace(/-/g, ' ')))
      );
      if (s > 0) scored.push({ id: 'imprint:' + slug, score: s * 0.7 });
    }

    var handle;
    for (handle in data.products) {
      if (!Object.prototype.hasOwnProperty.call(data.products, handle)) continue;
      node = data.products[handle];
      s = Math.max(
        scoreNameMatch(q, normalize(node.t)),
        scoreNameMatch(q, normalize(handle.replace(/-/g, ' ')))
      );
      if (s >= 40) scored.push({ id: 'product:' + handle, score: s * 0.85 });
    }

    scored.sort(function (a, b) {
      return b.score - a.score || (a.id < b.id ? -1 : 1);
    });

    var seen = {};
    var out = [];
    for (var i = 0; i < scored.length && out.length < 24; i++) {
      if (seen[scored[i].id]) continue;
      seen[scored[i].id] = true;
      out.push(scored[i]);
    }
    return out;
  }

  /** Filler words stripped before multi-entity intent parse. */
  var SEARCH_STOP = {
    a: 1,
    an: 1,
    and: 1,
    album: 1,
    albums: 1,
    by: 1,
    find: 1,
    for: 1,
    from: 1,
    get: 1,
    in: 1,
    like: 1,
    me: 1,
    music: 1,
    of: 1,
    or: 1,
    record: 1,
    records: 1,
    release: 1,
    releases: 1,
    show: 1,
    similar: 1,
    some: 1,
    song: 1,
    songs: 1,
    sound: 1,
    sounds: 1,
    the: 1,
    to: 1,
    want: 1,
    with: 1
  };

  /**
   * Best single entity hit for a phrase (prefers country/region/genre/artist).
   * @returns {{id:string, score:number, kind:string}|null}
   */
  function bestEntityForPhrase(phrase, preferKinds) {
    var linked = linkQuery(phrase) || [];
    if (!linked.length) return null;
    var prefer = preferKinds || null;
    var i;
    var hit;
    if (prefer && prefer.length) {
      for (i = 0; i < linked.length; i++) {
        hit = linked[i];
        if (!hit || !hit.id) continue;
        var kind = String(hit.id).split(':')[0];
        if (prefer.indexOf(kind) !== -1 && hit.score >= 40) {
          return { id: hit.id, score: hit.score, kind: kind };
        }
      }
    }
    hit = linked[0];
    if (!hit || !hit.id || !(hit.score >= 40)) return null;
    return {
      id: hit.id,
      score: hit.score,
      kind: String(hit.id).split(':')[0]
    };
  }

  /**
   * Parse natural queries into graph entities.
   * Examples: "desert blues like Tinariwen", "wedding music Morocco".
   * @returns {{
   *   raw: string,
   *   entities: Array<{id:string, score:number, kind:string, phrase:string}>,
   *   like: {phrase:string, entities:Array}|null,
   *   residual: string
   * }}
   */
  function parseSearchIntent(text) {
    var empty = {
      raw: String(text || ''),
      entities: [],
      like: null,
      residual: ''
    };
    if (!data) return empty;
    var q = normalize(text);
    if (!q || q.length < 2) return empty;

    var likePhrase = '';
    var likeRe =
      /\b(?:sounds?\s+like|similar\s+to|like|a\s+la|à\s+la)\s+(.+)$/i;
    var likeMatch = q.match(likeRe);
    if (likeMatch && likeMatch[1]) {
      likePhrase = normalize(likeMatch[1]);
      q = normalize(q.replace(likeMatch[0], ' '));
    }

    var tokens = q.split(' ').filter(function (t) {
      return t && !SEARCH_STOP[t];
    });
    var used = {};
    var entities = [];
    var seenId = {};

    function take(phrase, ent) {
      if (!ent || !ent.id || seenId[ent.id]) return false;
      seenId[ent.id] = true;
      entities.push({
        id: ent.id,
        score: ent.score,
        kind: ent.kind,
        phrase: phrase
      });
      return true;
    }

    // Greedy longest n-grams first (4 → 1).
    var maxLen = Math.min(4, tokens.length);
    var len;
    var i;
    for (len = maxLen; len >= 1; len--) {
      for (i = 0; i <= tokens.length - len; i++) {
        var blocked = false;
        var ti;
        for (ti = i; ti < i + len; ti++) {
          if (used[ti]) {
            blocked = true;
            break;
          }
        }
        if (blocked) continue;
        var phrase = tokens.slice(i, i + len).join(' ');
        var prefer =
          len >= 2
            ? ['country', 'region', 'genre', 'artist']
            : ['country', 'region', 'genre', 'artist', 'imprint'];
        // Single short tokens need a stronger hit (avoid "to"/"music" leftovers).
        var ent = bestEntityForPhrase(phrase, prefer);
        if (!ent) continue;
        if (len === 1 && ent.score < 50 && ent.kind === 'product') continue;
        if (len === 1 && ent.score < 45) continue;
        if (!take(phrase, ent)) continue;
        for (ti = i; ti < i + len; ti++) used[ti] = true;
      }
    }

    // Full-phrase fallback when nothing matched (keeps exactGenre / country aliases).
    if (!entities.length) {
      var full = bestEntityForPhrase(q, [
        'country',
        'region',
        'genre',
        'artist'
      ]);
      if (full) take(q, full);
    }

    var like = null;
    if (likePhrase) {
      var likeEnts = [];
      var likeLane = null;
      var likeHit = bestEntityForPhrase(likePhrase, [
        'artist',
        'product',
        'genre'
      ]);
      if (likeHit) {
        likeEnts.push({
          id: likeHit.id,
          score: likeHit.score,
          kind: likeHit.kind,
          phrase: likePhrase
        });
        if (likeHit.kind === 'artist') {
          var likeSlug = String(likeHit.id).split(':').slice(1).join(':');
          if (likeSlug && data.artists[likeSlug]) {
            likeLane = laneFromCatalogArtist(likeSlug, data.artists[likeSlug]);
          }
        }
      } else {
        // OOV / unstocked: seed neighbour lane from catalog fuzzy, baked
        // affinity, or (async) live MusicBrainz — never leave like.entities
        // empty when a lane is known.
        likeLane = resolveLikeLaneSync(likePhrase);
        if (likeLane) injectLaneEntities(likeEnts, likeLane, likePhrase);
      }
      like = { phrase: likePhrase, entities: likeEnts, lane: likeLane };
    }

    var residualParts = [];
    for (i = 0; i < tokens.length; i++) {
      if (!used[i]) residualParts.push(tokens[i]);
    }

    return {
      raw: String(text || ''),
      entities: entities,
      like: like,
      residual: residualParts.join(' ')
    };
  }

  /**
   * Score a product handle against parsed search intent (entity overlap + emb).
   */
  function scoreHandleForIntent(handle, intent, seedEmbs) {
    if (!data || !handle || !data.products[handle]) return 0;
    var p = data.products[handle];
    var score = 0;
    var ents = (intent && intent.entities) || [];
    var i;
    var gset = {};
    var cset = {};
    var rgset = {};
    if (p.g) {
      for (i = 0; i < p.g.length; i++) gset[p.g[i]] = true;
    }
    if (p.c) {
      for (i = 0; i < p.c.length; i++) cset[p.c[i]] = true;
    }
    if (p.rg) {
      for (i = 0; i < p.rg.length; i++) rgset[p.rg[i]] = true;
    }

    for (i = 0; i < ents.length; i++) {
      var ent = ents[i];
      if (!ent || !ent.id) continue;
      var parts = String(ent.id).split(':');
      var kind = parts[0];
      var key = parts.slice(1).join(':');
      var w = ent.score >= 80 ? 1 : 0.7;
      if (kind === 'genre' && gset[key]) score += 120 * w;
      else if (kind === 'country' && cset[key]) score += 130 * w;
      else if (kind === 'region' && rgset[key]) score += 110 * w;
      else if (kind === 'artist' && p.a === key) score += 160 * w;
      else if (
        kind === 'artist' &&
        data.artists[key] &&
        data.artists[key].r &&
        p.a &&
        data.artists[key].r.indexOf(p.a) !== -1
      ) {
        // Related-artist edge — well below exact artist (160) so neighbours
        // cannot outrank the named artist after engagement blend.
        score += 70 * w;
        // Same Rough Guide series (Jazz Legends) beats loose jazz co-tags
        // (Edith Piaf, Indo-Jazz) when the query artist is from that series.
        var seedPs = data.artists[key].p || [];
        var seedTitle = '';
        if (seedPs.length && data.products[seedPs[0]]) {
          seedTitle = String(data.products[seedPs[0]].t || '');
        }
        var pTitle = String(p.t || '');
        if (
          /jazz legends/i.test(seedTitle) &&
          /jazz legends/i.test(pTitle)
        ) {
          score += 45 * w;
        }
      }
      else if (kind === 'imprint' && p.i === key) score += 70 * w;
      else if (kind === 'product' && handle === key) score += 200;
      else if (kind === 'genre') {
        // Related genre soft match
        var rel = relatedGenres(key);
        var ri;
        for (ri = 0; ri < rel.length; ri++) {
          if (gset[rel[ri].slug]) {
            score += (rel[ri].kind === 'c' || rel[ri].kind === 'p' ? 55 : 35) * w;
            break;
          }
        }
      }
    }

    // Thin-shelf densify: must score ≥100 so discover's artist-only filter
    // (related <100 dropped when exact artist is pinned) keeps the shelf.
    if (intent && intent.densifyGenres && intent.densifyGenres.length) {
      var dgi;
      for (dgi = 0; dgi < intent.densifyGenres.length; dgi++) {
        if (gset[intent.densifyGenres[dgi]]) {
          score += 110;
          break;
        }
      }
    }
    if (
      intent &&
      intent.densifyArtists &&
      intent.densifyArtists.length &&
      p.a &&
      intent.densifyArtists.indexOf(p.a) !== -1
    ) {
      score += 105;
    }
    if (
      intent &&
      intent.densifyEmb &&
      intent.densifyEmb.length &&
      intent.densifyEmb.indexOf(handle) !== -1
    ) {
      score += 100;
    }

    // "like Artist" — boost products sharing genres with seed, or emb near seed.
    if (intent && intent.like && intent.like.entities) {
      var li;
      for (li = 0; li < intent.like.entities.length; li++) {
        var le = intent.like.entities[li];
        if (!le) continue;
        var lk = String(le.id).split(':');
        if (lk[0] === 'artist' && p.a === lk[1]) score += 220;
        if (lk[0] === 'product' && handle === lk[1]) score += 220;
        if (lk[0] === 'genre' && gset[lk[1]]) score += 90;
        if (lk[0] === 'artist' && data.artists[lk[1]] && data.artists[lk[1]].g) {
          var ag = data.artists[lk[1]].g;
          var ai;
          for (ai = 0; ai < ag.length; ai++) {
            if (gset[ag[ai]]) {
              score += 90;
              break;
            }
          }
        }
      }
    }
    // OOV like-lane (affinity / live MB) — seed artists + lane genres.
    if (intent && intent.like && intent.like.lane) {
      var lane = intent.like.lane;
      if (lane.seeds && lane.seeds.length && p.a && lane.seeds.indexOf(p.a) !== -1) {
        score += 160;
      }
      if (lane.g && lane.g.length) {
        var lgi;
        for (lgi = 0; lgi < lane.g.length; lgi++) {
          if (gset[lane.g[lgi]]) {
            score += 95;
            break;
          }
        }
      }
    }

    if (seedEmbs && seedEmbs.length) {
      var pe = getProductEmbedding(handle);
      if (pe && pe.length) {
        var best = 0;
        var si;
        for (si = 0; si < seedEmbs.length; si++) {
          var d = dotEmbedding(pe, seedEmbs[si]);
          if (d > best) best = d;
        }
        if (best > 0) score += best * 80;
      }
    }

    return score;
  }

  /**
   * Genre seeds when a country has few country:* tags.
   * Keep aligned with engagement COUNTRY_GENRE_BRIDGES (search densify).
   */
  var SEARCH_COUNTRY_GENRE_BRIDGES = {
    india: ['indian-classical-raga', 'bollywood-filmi'],
    cuba: ['afro-cuban-son', 'salsa'],
    brazil: ['brazilian'],
    jamaica: ['reggae-dancehall-ragga'],
    mali: ['desert-blues-sahara', 'tuareg'],
    argentina: ['tango'],
    morocco: ['gnawa'],
    portugal: ['fado-portuguese', 'flamenco'],
    spain: ['flamenco', 'fado-portuguese'],
    peru: ['cumbia'],
    senegal: ['afrobeat'],
    nigeria: ['afrobeat', 'highlife'],
    ghana: ['highlife'],
    turkey: ['arabic-middle-eastern']
  };
  var SEARCH_COUNTRY_ADJ_TOKENS = {
    india: ['indian'],
    brazil: ['brazilian', 'brasil'],
    cuba: ['cuban', 'afro-cuban'],
    jamaica: ['jamaican'],
    mali: ['malian'],
    senegal: ['senegalese'],
    ghana: ['ghanaian'],
    nigeria: ['nigerian'],
    ethiopia: ['ethiopian', 'ethio'],
    morocco: ['moroccan'],
    portugal: ['portuguese', 'fado'],
    spain: ['spanish', 'flamenco'],
    argentina: ['argentinian', 'argentine', 'tango'],
    peru: ['peruvian', 'andean'],
    cambodia: ['cambodian'],
    madagascar: ['malagasy'],
    haiti: ['haitian'],
    japan: ['japanese'],
    turkey: ['turkish'],
    'south-africa': ['south-african', 'mbaqanga']
  };
  var SEARCH_GENERIC_DENSIFY_GENRES = {
    folk: 1,
    jazz: 1,
    blues: 1,
    country: 1,
    gospel: 1,
    'gospel-spiritual': 1,
    roots: 1,
    acoustic: 1,
    world: 1,
    'world-music': 1,
    'world-compilation': 1,
    'world-music-compilation': 1,
    instrumental: 1,
    vocals: 1,
    pop: 1,
    rock: 1,
    classical: 1,
    soundtrack: 1
  };
  var THIN_SHELF_MIN = 8;

  function isGenericDensifyGenre(slug) {
    return !!(slug && SEARCH_GENERIC_DENSIFY_GENRES[slug]);
  }

  /**
   * Genre keys for a thin country query — explicit bridges + adjective
   * slug/name match only. Never harvest co-tags from the country's few
   * products (Cuban rare-groove comps also carry psychedelic/reggae).
   */
  function countryDensifyGenres(countrySlug) {
    if (!countrySlug || !data || !data.genres) return [];
    var out = [];
    var seen = {};
    function push(g) {
      if (!g || seen[g] || isGenericDensifyGenre(g)) return;
      if (!data.genres[g]) return;
      // Noisy catch-all style tags — never densify countries through these.
      if (
        g === 'psychedelic-rare-groove' ||
        (g === 'reggae-dancehall-ragga' && countrySlug !== 'jamaica')
      ) {
        return;
      }
      seen[g] = true;
      out.push(g);
    }
    var bridges = SEARCH_COUNTRY_GENRE_BRIDGES[countrySlug] || [];
    var bi;
    for (bi = 0; bi < bridges.length; bi++) push(bridges[bi]);
    var tokens = [countrySlug].concat(
      SEARCH_COUNTRY_ADJ_TOKENS[countrySlug] || []
    );
    var slug;
    for (slug in data.genres) {
      if (!Object.prototype.hasOwnProperty.call(data.genres, slug)) continue;
      if (isGenericDensifyGenre(slug)) continue;
      var nameN = normalize(data.genres[slug].n || slug.replace(/-/g, ' '));
      var slugN = normalize(slug.replace(/-/g, ' '));
      var ti;
      for (ti = 0; ti < tokens.length; ti++) {
        var tok = normalize(tokens[ti]);
        if (!tok || tok.length < 4) continue;
        // Prefer whole-token genre matches (cuban → afro-cuban-son),
        // not substring accidents inside unrelated slugs.
        if (
          slugN === tok ||
          slugN.indexOf(tok + ' ') === 0 ||
          slugN.indexOf(' ' + tok) !== -1 ||
          nameN === tok ||
          nameN.indexOf(tok + ' ') === 0 ||
          nameN.indexOf(' ' + tok) !== -1 ||
          slug === tok.replace(/\s+/g, '-') ||
          slug.indexOf(tok.replace(/\s+/g, '-') + '-') === 0 ||
          slug.indexOf('-' + tok.replace(/\s+/g, '-')) !== -1
        ) {
          push(slug);
          break;
        }
      }
    }
    return out.slice(0, 4);
  }

  function pushUniqueId(arr, id) {
    if (!id || arr.indexOf(id) !== -1) return;
    arr.push(id);
  }

  function pushUniqueGenre(list, g) {
    if (!g || list.indexOf(g) !== -1) return;
    if (!data.genres || !data.genres[g]) return;
    list.push(g);
  }

  /**
   * Sibling / child genres for densify (skip jazz/blues/folk/world dumps).
   */
  function densifySiblingGenres(genreSlug, out, limit) {
    limit = limit != null ? limit : 4;
    var rels = relatedGenres(genreSlug, ['c', 's']);
    var i;
    for (i = 0; i < rels.length && out.length < limit + 8; i++) {
      if (isGenericDensifyGenre(rels[i].slug)) continue;
      pushUniqueGenre(out, rels[i].slug);
    }
  }

  /**
   * Keep densify shelves tight: drop mega-comps with 5+ genre tags unless
   * they also carry the queried country.
   */
  function densifyProductAllowed(handle, densifyGenres, countrySlug) {
    if (!data || !data.products || !data.products[handle]) return false;
    var p = data.products[handle];
    var g = p.g || [];
    var c = p.c || [];
    if (countrySlug && c.indexOf(countrySlug) !== -1) return true;
    // Tagged for a different country (lost-in-mali on brazilian lane) — skip.
    if (countrySlug && c.length && c.indexOf(countrySlug) === -1) return false;
    var hit = false;
    var i;
    if (densifyGenres && densifyGenres.length) {
      for (i = 0; i < densifyGenres.length; i++) {
        if (g.indexOf(densifyGenres[i]) !== -1) {
          hit = true;
          break;
        }
      }
    } else {
      hit = true;
    }
    if (!hit) return false;
    // Multi-genre Rough Guide dumps (women-of-the-world, best-you've-never-heard).
    if (g.length >= 4) return false;
    return true;
  }

  /**
   * Embedding neighbours of seed products — last resort when genre lanes are
   * too sparse (Johnny Cash: only 3 country-tagged titles in the catalog).
   */
  function densifyEmbHandles(
    seedProductHandles,
    existingHandles,
    limit,
    preferGenres
  ) {
    limit = limit != null ? limit : 10;
    if (!data || !data.products) return [];
    var seeds = [];
    var i;
    for (i = 0; i < (seedProductHandles || []).length && seeds.length < 3; i++) {
      var vec = getProductEmbedding(seedProductHandles[i]);
      if (vec && vec.length) seeds.push(vec);
    }
    if (!seeds.length) return [];
    var scored = [];
    var h;
    for (h in data.products) {
      if (!Object.prototype.hasOwnProperty.call(data.products, h)) continue;
      if (existingHandles[h]) continue;
      var pe = getProductEmbedding(h);
      if (!pe) continue;
      var best = 0;
      var si;
      for (si = 0; si < seeds.length; si++) {
        var d = dotEmbedding(pe, seeds[si]);
        if (d > best) best = d;
      }
      var p = data.products[h];
      var pg = (p && p.g) || [];
      if (pg.length >= 5) continue;
      var genreShare = false;
      if (preferGenres && preferGenres.length) {
        for (si = 0; si < preferGenres.length; si++) {
          if (pg.indexOf(preferGenres[si]) !== -1) {
            genreShare = true;
            break;
          }
        }
      }
      // Shared lane genre only when preferGenres is set — never emb-pull
      // Peru rare-groove into Cuba just because covers are similar.
      if (preferGenres && preferGenres.length) {
        if (genreShare && best >= 0.38) scored.push({ h: h, d: best + 0.05 });
      } else if (best >= 0.55) {
        scored.push({ h: h, d: best });
      }
    }
    scored.sort(function (a, b) {
      return b.d - a.d || (a.h < b.h ? -1 : 1);
    });
    var out = [];
    for (i = 0; i < scored.length && out.length < limit; i++) {
      out.push(scored[i].h);
    }
    return out;
  }

  /**
   * When artist/country expand yields a tiny shelf, add neighbour artists,
   * genre product pools + siblings, then emb neighbours — never dump
   * generic jazz/blues/world-music pools (Coltrane / France / Belarus).
   */
  function densifyThinExpandIds(intent, expandIds, handleCount) {
    if (!intent || !intent.entities || !intent.entities.length) return expandIds;
    if (handleCount >= THIN_SHELF_MIN) return expandIds;
    if (intent.like) return expandIds;

    var artistOnly =
      intent.entities.length > 0 &&
      intent.entities.every(function (e) {
        return e && String(e.id || '').indexOf('artist:') === 0;
      });
    var countryOnly =
      intent.entities.length > 0 &&
      intent.entities.every(function (e) {
        return e && String(e.id || '').indexOf('country:') === 0;
      });

    if (artistOnly) {
      var densifyArtists = [];
      var densifyGenres = [];
      var seedProducts = [];
      var ai;
      for (ai = 0; ai < intent.entities.length; ai++) {
        var aSlug = String(intent.entities[ai].id).split(':').slice(1).join(':');
        var anode = data.artists && data.artists[aSlug];
        if (!anode) continue;
        var seeds = (anode.r || []).slice();
        var ag = (anode.g || []).filter(function (g) {
          return !isGenericDensifyGenre(g);
        });
        // Prefer specific genres; fall back to the sole tag (Cash → country).
        var laneG = ag.length ? ag : (anode.g || []).slice(0, 1);
        if (seeds.length < 4 && laneG.length) {
          var live = genreNeighborArtists(laneG, aSlug, 8);
          var li;
          for (li = 0; li < live.length; li++) {
            if (seeds.indexOf(live[li]) === -1) seeds.push(live[li]);
          }
        }
        var si;
        for (si = 0; si < seeds.length && densifyArtists.length < 8; si++) {
          if (seeds[si] && densifyArtists.indexOf(seeds[si]) === -1) {
            densifyArtists.push(seeds[si]);
            pushUniqueId(expandIds, 'artist:' + seeds[si]);
          }
        }
        for (si = 0; si < laneG.length; si++) {
          pushUniqueGenre(densifyGenres, laneG[si]);
          // Genre product pool (depth 0 later) — fills when no neighbour artists.
          pushUniqueId(expandIds, 'genre:' + laneG[si]);
          // Only close siblings (c/s); skip brazilian/tango fan-out for salsa
          // when the artist lane is already salsa — keep afro-cuban only.
          if (laneG[si] === 'salsa' || laneG[si] === 'afro-cuban-son') {
            pushUniqueGenre(densifyGenres, 'afro-cuban-son');
            pushUniqueGenre(densifyGenres, 'salsa');
          } else {
            densifySiblingGenres(laneG[si], densifyGenres, 3);
          }
        }
        for (si = 0; si < densifyGenres.length; si++) {
          pushUniqueId(expandIds, 'genre:' + densifyGenres[si]);
        }
        if (anode.p) {
          for (si = 0; si < anode.p.length; si++) seedProducts.push(anode.p[si]);
        }
      }
      if (densifyArtists.length) intent.densifyArtists = densifyArtists;
      if (densifyGenres.length) intent.densifyGenres = densifyGenres;
      if (seedProducts.length) intent.densifySeedProducts = seedProducts;
      return expandIds;
    }

    if (countryOnly) {
      // Bridges + adjective matches only — no sibling genre fan-out
      // (brazilian siblings pulled Mali/world comps into Cuba).
      var densifyG = [];
      var seedProducts = [];
      var ci;
      for (ci = 0; ci < intent.entities.length; ci++) {
        var cSlug = String(intent.entities[ci].id).split(':').slice(1).join(':');
        var cg = countryDensifyGenres(cSlug);
        var cgi;
        for (cgi = 0; cgi < cg.length; cgi++) {
          pushUniqueGenre(densifyG, cg[cgi]);
        }
        var cnode = data.countries && data.countries[cSlug];
        if (cnode && cnode.p) {
          for (cgi = 0; cgi < cnode.p.length; cgi++) {
            seedProducts.push(cnode.p[cgi]);
          }
        }
      }
      if (densifyG.length) intent.densifyGenres = densifyG;
      if (seedProducts.length) intent.densifySeedProducts = seedProducts;
      return expandIds;
    }

    return expandIds;
  }

  /**
   * Graph-powered candidate search for natural-language queries.
   * @param {string} text
   * @param {{limit?:number, seedHandles?:string[], likeLane?:object}} [opts]
   * @returns {{
   *   intent: object,
   *   handles: string[],
   *   scores: Object.<string, number>,
   *   seeded: boolean
   * }}
   */
  function graphSearch(text, opts) {
    opts = opts || {};
    var limit = opts.limit != null ? opts.limit : 48;
    var intent = parseSearchIntent(text);
    var scores = {};
    var handles = {};
    var seeded = false;

    if (!data) {
      return { intent: intent, handles: [], scores: scores, seeded: false };
    }

    // Inject async-resolved OOV lane (live MB / late affinity) into intent.
    if (opts.likeLane && opts.likeLane.g && opts.likeLane.g.length) {
      if (!intent.like) {
        intent.like = { phrase: '', entities: [], lane: opts.likeLane };
      } else {
        intent.like.lane = opts.likeLane;
        if (!intent.like.entities || !intent.like.entities.length) {
          intent.like.entities = [];
          injectLaneEntities(
            intent.like.entities,
            opts.likeLane,
            intent.like.phrase || ''
          );
        }
      }
    }

    var expandIds = [];
    var ei;
    for (ei = 0; ei < intent.entities.length; ei++) {
      expandIds.push(intent.entities[ei].id);
      // Soft-expand related genres one hop for style queries
      var eParts = String(intent.entities[ei].id).split(':');
      if (eParts[0] === 'genre') {
        var rels = relatedGenres(eParts[1], ['c', 's']);
        var rj;
        for (rj = 0; rj < rels.length && rj < 4; rj++) {
          expandIds.push('genre:' + rels[rj].slug);
        }
      }
    }
    if (intent.like && intent.like.entities) {
      for (ei = 0; ei < intent.like.entities.length; ei++) {
        expandIds.push(intent.like.entities[ei].id);
      }
    }
    // Tight OOV lane: expand seed artists + lane genres (depth 1 via expand).
    // Do not expand countries alone — that dumps unrelated comps.
    if (intent.like && intent.like.lane) {
      var laneExpand = intent.like.lane;
      var lxi;
      if (laneExpand.seeds) {
        for (lxi = 0; lxi < laneExpand.seeds.length; lxi++) {
          expandIds.push('artist:' + laneExpand.seeds[lxi]);
        }
      }
      if (laneExpand.g) {
        for (lxi = 0; lxi < laneExpand.g.length; lxi++) {
          expandIds.push('genre:' + laneExpand.g[lxi]);
        }
      }
    }

    var seedHandles = opts.seedHandles || [];
    if (seedHandles.length) seeded = true;
    for (ei = 0; ei < seedHandles.length; ei++) {
      if (seedHandles[ei]) expandIds.push('product:' + seedHandles[ei]);
    }

    if (expandIds.length) {
      var expanded = expand(expandIds, 1) || [];
      for (ei = 0; ei < expanded.length; ei++) handles[expanded[ei]] = true;
    }

    // Thin artist / country: densify into a usable shelf.
    var handleCount = Object.keys(handles).length;
    densifyThinExpandIds(intent, expandIds, handleCount);
    var densifyCountrySlug = '';
    if (
      intent.entities.length === 1 &&
      String(intent.entities[0].id || '').indexOf('country:') === 0
    ) {
      densifyCountrySlug = String(intent.entities[0].id).split(':').slice(1).join(':');
    }
    if (intent.densifyArtists && intent.densifyArtists.length) {
      var artistDensifyIds = [];
      for (ei = 0; ei < intent.densifyArtists.length; ei++) {
        artistDensifyIds.push('artist:' + intent.densifyArtists[ei]);
      }
      var artistDensified = expand(artistDensifyIds, 1) || [];
      for (ei = 0; ei < artistDensified.length; ei++) {
        if (
          densifyProductAllowed(
            artistDensified[ei],
            intent.densifyGenres,
            densifyCountrySlug
          )
        ) {
          handles[artistDensified[ei]] = true;
        }
      }
    }
    if (intent.densifyGenres && intent.densifyGenres.length) {
      // Depth 0 only — genre.p, never genre.a dump (Coltrane/Cuba purity).
      var genreDensifyIds = [];
      for (ei = 0; ei < intent.densifyGenres.length; ei++) {
        genreDensifyIds.push('genre:' + intent.densifyGenres[ei]);
      }
      var genreDensified = expand(genreDensifyIds, 0) || [];
      for (ei = 0; ei < genreDensified.length; ei++) {
        if (
          densifyProductAllowed(
            genreDensified[ei],
            intent.densifyGenres,
            densifyCountrySlug
          )
        ) {
          handles[genreDensified[ei]] = true;
        }
      }
    }
    // Last resort: embedding neighbours of the thin seed product(s).
    // Require a densify genre lane — otherwise France/Piaf emb-dumps jazz.
    if (
      Object.keys(handles).length < THIN_SHELF_MIN &&
      intent.densifySeedProducts &&
      intent.densifySeedProducts.length &&
      intent.densifyGenres &&
      intent.densifyGenres.length
    ) {
      var embExtra = densifyEmbHandles(
        intent.densifySeedProducts,
        handles,
        THIN_SHELF_MIN,
        intent.densifyGenres
      );
      for (ei = 0; ei < embExtra.length; ei++) {
        handles[embExtra[ei]] = true;
      }
      if (embExtra.length) intent.densifyEmb = embExtra;
    }

    // Seed embs from like-artist products or explicit seeds
    var seedEmbs = [];
    function pushEmb(h) {
      var vec = getProductEmbedding(h);
      if (vec && vec.length) seedEmbs.push(vec);
    }
    for (ei = 0; ei < seedHandles.length && seedEmbs.length < 4; ei++) {
      pushEmb(seedHandles[ei]);
    }
    if (intent.like && intent.like.entities) {
      for (ei = 0; ei < intent.like.entities.length; ei++) {
        var lid = intent.like.entities[ei].id;
        var lp = String(lid).split(':');
        if (lp[0] === 'product') pushEmb(lp[1]);
        if (lp[0] === 'artist' && data.artists[lp[1]] && data.artists[lp[1]].p) {
          var ap = data.artists[lp[1]].p;
          var api;
          for (api = 0; api < ap.length && seedEmbs.length < 4; api++) {
            pushEmb(ap[api]);
          }
        }
      }
    }
    if (intent.like && intent.like.lane && intent.like.lane.seeds) {
      for (ei = 0; ei < intent.like.lane.seeds.length && seedEmbs.length < 4; ei++) {
        var seedSlug = intent.like.lane.seeds[ei];
        if (data.artists[seedSlug] && data.artists[seedSlug].p) {
          var sap = data.artists[seedSlug].p;
          if (sap[0]) pushEmb(sap[0]);
        }
      }
    }

    // Always score expanded set; if empty but we have genre/country entities,
    // expand alone should have filled — if still empty, nothing to rank.
    var ranked = [];
    var h;
    // Artist-only queries: keep the artist + related; drop zero-score noise.
    var artistOnly =
      intent.entities.length > 0 &&
      intent.entities.every(function (e) {
        return e && String(e.id || '').indexOf('artist:') === 0;
      }) &&
      !intent.like;
    for (h in handles) {
      if (!Object.prototype.hasOwnProperty.call(handles, h)) continue;
      var sc = scoreHandleForIntent(h, intent, seedEmbs);
      if (sc > 0) {
        scores[h] = sc;
        ranked.push(h);
      } else if (!artistOnly && seedEmbs.length) {
        // Soft keep when we have embedding seeds (like-X) and emb overlap.
        var peSoft = getProductEmbedding(h);
        var embSoft = 0;
        var si;
        for (si = 0; si < seedEmbs.length; si++) {
          var dSoft = dotEmbedding(peSoft, seedEmbs[si]);
          if (dSoft > embSoft) embSoft = dSoft;
        }
        if (embSoft >= 0.35) {
          scores[h] = Math.round(embSoft * 40);
          ranked.push(h);
        }
      }
    }

    // If intent linked entities / OOV like-lane but expand was empty
    // (e.g. country missing), scan products for soft field matches.
    var hasLikeLane = !!(intent.like && intent.like.lane && intent.like.lane.g);
    if (!ranked.length && (intent.entities.length || hasLikeLane)) {
      for (h in data.products) {
        if (!Object.prototype.hasOwnProperty.call(data.products, h)) continue;
        var soft = scoreHandleForIntent(h, intent, seedEmbs);
        if (soft >= 50) {
          scores[h] = soft;
          ranked.push(h);
        }
      }
    }

    ranked.sort(function (a, b) {
      return (scores[b] || 0) - (scores[a] || 0) || (a < b ? -1 : 1);
    });
    if (ranked.length > limit) ranked = ranked.slice(0, limit);

    return {
      intent: intent,
      handles: ranked,
      scores: scores,
      seeded: seeded
    };
  }

  /**
   * Build a slim discover card payload from a graph product handle.
   */
  function productCard(handle) {
    if (!data || !handle || !data.products[handle]) return null;
    var p = data.products[handle];
    var artistName = '';
    if (p.a && data.artists[p.a] && data.artists[p.a].n) {
      artistName = data.artists[p.a].n;
    } else if (p.a) {
      artistName = String(p.a).replace(/-/g, ' ');
    }
    var root = shopRoot();
    if (root.slice(-1) !== '/') root += '/';
    return {
      handle: handle,
      title: p.t || handle,
      artist: artistName,
      imprint: p.i || '',
      url: root + 'products/' + encodeURIComponent(handle),
      image: getProductImage(handle) || null,
      genres: Array.isArray(p.g) ? p.g.slice() : []
    };
  }

  function shopRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  var imageCache = {};

  function normalizeImageUrl(src) {
    if (!src || src === 'NULL') return null;
    var s = String(src);
    if (s.indexOf('//') === 0) s = 'https:' + s;
    return s;
  }

  function withImageWidth(url, width) {
    if (!url) return null;
    var w = width || 200;
    var s = normalizeImageUrl(url);
    if (!s) return null;
    if (/_[0-9]+x(\.|[?])/.test(s)) {
      return s.replace(/_[0-9]+x/, '_' + w + 'x');
    }
    // Avoid double-insert if already sized via CDN path
    if (s.indexOf('_' + w + 'x.') !== -1) return s;
    return s.replace(/(\.(jpg|jpeg|png|webp|gif))(\?|$)/i, '_' + w + 'x$1$3');
  }

  function extractProductImage(prod) {
    if (!prod) return null;
    var src =
      (prod.featured_image &&
        (typeof prod.featured_image === 'string'
          ? prod.featured_image
          : prod.featured_image.src)) ||
      (prod.images &&
        prod.images[0] &&
        (typeof prod.images[0] === 'string'
          ? prod.images[0]
          : prod.images[0].src)) ||
      (prod.media &&
        prod.media[0] &&
        (prod.media[0].src ||
          (prod.media[0].preview_image && prod.media[0].preview_image.src))) ||
      null;
    return withImageWidth(normalizeImageUrl(src), 200);
  }

  /** Cached product image URL, or null until hydrateImages fills it. '' = failed. */
  function getProductImage(handle) {
    if (!handle) return null;
    // Prefer warm cache, but never let a failed '' block baked graph covers.
    if (Object.prototype.hasOwnProperty.call(imageCache, handle) && imageCache[handle]) {
      return imageCache[handle];
    }
    // Baked graph field — instant Dig deeper / For you covers without product.js hydrate.
    if (data && data.products && data.products[handle] && data.products[handle].img) {
      var baked =
        withImageWidth(normalizeImageUrl(data.products[handle].img), 200) ||
        data.products[handle].img;
      imageCache[handle] = baked;
      return baked;
    }
    if (Object.prototype.hasOwnProperty.call(imageCache, handle)) {
      return imageCache[handle] || null;
    }
    return null;
  }

  function hasImageCacheEntry(handle) {
    if (!handle) return false;
    if (Object.prototype.hasOwnProperty.call(imageCache, handle)) return true;
    return !!(data && data.products && data.products[handle] && data.products[handle].img);
  }

  function searchImageByTitle(title, preferHandle) {
    if (!title) return Promise.reject(new Error('no title'));
    var root = shopRoot();
    var url =
      root +
      'search?type=product&q=' +
      encodeURIComponent(title) +
      '*&view=json';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('search ' + res.status);
        return res.json();
      })
      .then(function (payload) {
        var results = (payload && payload.results) || [];
        var i;
        var titleL = String(title).toLowerCase();
        for (i = 0; i < results.length; i++) {
          if (preferHandle && results[i].handle === preferHandle) {
            return withImageWidth(
              normalizeImageUrl(results[i].thumbnail || results[i].image),
              200
            );
          }
        }
        for (i = 0; i < results.length; i++) {
          if (String(results[i].title || '').toLowerCase() === titleL) {
            return withImageWidth(
              normalizeImageUrl(results[i].thumbnail || results[i].image),
              200
            );
          }
        }
        for (i = 0; i < results.length; i++) {
          var img = results[i].thumbnail || results[i].image;
          if (img && img !== 'NULL') {
            return withImageWidth(normalizeImageUrl(img), 200);
          }
        }
        return null;
      });
  }

  function fetchImageForHandle(handle) {
    var root = shopRoot();
    var title =
      data && data.products && data.products[handle] && data.products[handle].t
        ? data.products[handle].t
        : '';
    return fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('img ' + res.status);
        return res.json();
      })
      .then(function (prod) {
        var src = extractProductImage(prod);
        if (!src) throw new Error('no img field');
        return src;
      })
      .catch(function () {
        return searchImageByTitle(title, handle);
      });
  }

  /**
   * Fetch images for missing covers. Marks failures as '' so callers can skip blanks.
   * Concurrency-limited so live search fetches aren't starved.
   */
  function hydrateImages(handles) {
    var list = [];
    var i;
    for (i = 0; i < (handles || []).length; i++) {
      var h = handles[i];
      if (h && !hasImageCacheEntry(h)) list.push(h);
    }
    if (!list.length) return Promise.resolve();

    var CONCURRENCY = 8;
    var idx = 0;

    function next() {
      if (idx >= list.length) return Promise.resolve();
      var handle = list[idx++];
      return fetchImageForHandle(handle)
        .then(function (src) {
          imageCache[handle] = src || '';
        })
        .catch(function () {
          imageCache[handle] = '';
        })
        .then(next);
    }

    var workers = [];
    for (i = 0; i < Math.min(CONCURRENCY, list.length); i++) {
      workers.push(next());
    }
    return Promise.all(workers);
  }

  /** Warm graph on first search focus (lazy — not on every page paint). */
  function armLazyTriggers() {
    if (window.__wmnGraphArmed) return;
    window.__wmnGraphArmed = true;

    function warm() {
      ensureLoaded().catch(function () {
        /* silent — rankers degrade without graph */
      });
    }

    document.addEventListener(
      'focusin',
      function (ev) {
        var t = ev.target;
        if (!t) return;
        if (
          t.matches &&
          t.matches(
            'input[type="search"], input[name="q"], .search-input, #search-field, [data-wmn-search]'
          )
        ) {
          warm();
        }
      },
      true
    );

    document.addEventListener('wmn:graph-prefetch', warm);

    // Prefetch during idle so first search focus is instant.
    if (window.requestIdleCallback) {
      window.requestIdleCallback(warm, { timeout: 2500 });
    } else {
      setTimeout(warm, 1500);
    }
  }

  armLazyTriggers();

  /**
   * Minimal sync SHA-256 (hash-trigram-v1 must match bake_catalog_embeddings.py).
   */
  function sha256Hex(str) {
    function rotr(n, x) {
      return (x >>> n) | (x << (32 - n));
    }
    var K = [
      0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
      0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
      0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
      0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
      0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
      0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
      0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
      0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
      0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
      0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
      0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ];
    var bytes = [];
    var i;
    for (i = 0; i < str.length; i++) {
      var c = str.charCodeAt(i);
      if (c < 0x80) bytes.push(c);
      else if (c < 0x800) {
        bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
      } else {
        bytes.push(
          0xe0 | (c >> 12),
          0x80 | ((c >> 6) & 0x3f),
          0x80 | (c & 0x3f)
        );
      }
    }
    var bitLen = bytes.length * 8;
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (i = 7; i >= 0; i--) bytes.push((bitLen / Math.pow(2, i * 8)) & 0xff);

    var H = [
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
      0x1f83d9ab, 0x5be0cd19
    ];
    var w = new Array(64);
    var chunk;
    for (chunk = 0; chunk < bytes.length; chunk += 64) {
      for (i = 0; i < 16; i++) {
        var j = chunk + i * 4;
        w[i] =
          (bytes[j] << 24) |
          (bytes[j + 1] << 16) |
          (bytes[j + 2] << 8) |
          bytes[j + 3];
      }
      for (i = 16; i < 64; i++) {
        var s0 =
          rotr(7, w[i - 15]) ^ rotr(18, w[i - 15]) ^ (w[i - 15] >>> 3);
        var s1 =
          rotr(17, w[i - 2]) ^ rotr(19, w[i - 2]) ^ (w[i - 2] >>> 10);
        w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
      }
      var a = H[0],
        b = H[1],
        c2 = H[2],
        d = H[3],
        e = H[4],
        f = H[5],
        g = H[6],
        h = H[7];
      for (i = 0; i < 64; i++) {
        var S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
        var ch = (e & f) ^ (~e & g);
        var t1 = (h + S1 + ch + K[i] + w[i]) | 0;
        var S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
        var maj = (a & b) ^ (a & c2) ^ (b & c2);
        var t2 = (S0 + maj) | 0;
        h = g;
        g = f;
        f = e;
        e = (d + t1) | 0;
        d = c2;
        c2 = b;
        b = a;
        a = (t1 + t2) | 0;
      }
      H[0] = (H[0] + a) | 0;
      H[1] = (H[1] + b) | 0;
      H[2] = (H[2] + c2) | 0;
      H[3] = (H[3] + d) | 0;
      H[4] = (H[4] + e) | 0;
      H[5] = (H[5] + f) | 0;
      H[6] = (H[6] + g) | 0;
      H[7] = (H[7] + h) | 0;
    }
    var hex = '';
    for (i = 0; i < 8; i++) {
      var v = (H[i] >>> 0).toString(16);
      while (v.length < 8) v = '0' + v;
      hex += v;
    }
    return hex;
  }

  function hashBucket(token, dim) {
    var h = sha256Hex(token);
    return parseInt(h.slice(0, 8), 16) % dim;
  }

  function hashSign(token) {
    var h = sha256Hex('s:' + token);
    return parseInt(h.charAt(0), 16) % 2 === 0 ? 1 : -1;
  }

  /**
   * Same recipe as bake_catalog_embeddings.py (hash-trigram-v1).
   * @returns {number[]|null}
   */
  function embedText(text) {
    var dim = (data && data.emb && data.emb.d) || 64;
    var vec = new Array(dim);
    var i;
    for (i = 0; i < dim; i++) vec[i] = 0;
    var normed = normalize(text);
    if (!normed) return vec;
    var words = normed.split(' ');
    var wi;
    for (wi = 0; wi < words.length; wi++) {
      var w = words[wi];
      if (!w) continue;
      var tok = 'w:' + w;
      vec[hashBucket(tok, dim)] += hashSign(tok);
      if (w.length >= 3) {
        var padded = '^' + w + '$';
        var ti;
        for (ti = 0; ti < padded.length - 2; ti++) {
          var tg = 't:' + padded.slice(ti, ti + 3);
          vec[hashBucket(tg, dim)] += hashSign(tg);
        }
      }
    }
    var n2 = 0;
    for (i = 0; i < dim; i++) n2 += vec[i] * vec[i];
    n2 = Math.sqrt(n2) || 1;
    for (i = 0; i < dim; i++) vec[i] /= n2;
    return vec;
  }

  var affinityCache = null;
  var affinityPromise = null;
  var liveAffinityCache = Object.create(null);
  var liveAffinityInflight = Object.create(null);

  function affinityUrl() {
    var meta = document.querySelector('meta[name="wmn-artist-affinity-url"]');
    if (meta && meta.getAttribute('content')) return meta.getAttribute('content');
    // Derive from catalog-graph asset URL when possible
    var src = graphUrl() || '';
    if (src && src.indexOf('wmn-catalog-graph.json') !== -1) {
      return src.replace('wmn-catalog-graph.json', 'wmn-artist-affinity.json');
    }
    return null;
  }

  var PREFS_PROXY_FALLBACK = 'https://wmn-prefs-sync.vercel.app';

  function prefsProxyBase() {
    try {
      var meta = document.querySelector('meta[name="wmn-prefs-sync-proxy"]');
      var u = meta && meta.getAttribute('content');
      if (u) return String(u).replace(/\/$/, '');
    } catch (e) {
      /* ignore */
    }
    // Live MB affinity must work for anonymous search even when the
    // prefs-sync meta is absent (setting only used to be logged-in).
    return PREFS_PROXY_FALLBACK;
  }

  /** Affinity-shaped lane from an in-catalog artist node. */
  function laneFromCatalogArtist(slug, node) {
    if (!node) return null;
    var g = (node.g || []).slice();
    if (!g.length) return null;
    var seeds = (node.r || []).slice(0, 8);
    if (seeds.length < 4) {
      var live = genreNeighborArtists(g, slug, 8);
      var li;
      for (li = 0; li < live.length; li++) {
        if (seeds.indexOf(live[li]) === -1) seeds.push(live[li]);
      }
      if (seeds.length > 8) seeds = seeds.slice(0, 8);
    }
    return {
      n: node.n || slug,
      g: g,
      c: countriesForArtist(slug),
      seeds: seeds,
      exclude: slug,
      src: 'catalog-artist'
    };
  }

  /** Push catalog-backed genre/artist ids from a like-lane into like.entities. */
  function injectLaneEntities(likeEnts, lane, phrase) {
    if (!likeEnts || !lane) return;
    var seen = {};
    var i;
    for (i = 0; i < likeEnts.length; i++) {
      if (likeEnts[i] && likeEnts[i].id) seen[likeEnts[i].id] = true;
    }
    function push(id, score, kind) {
      if (!id || seen[id]) return;
      seen[id] = true;
      likeEnts.push({
        id: id,
        score: score,
        kind: kind,
        phrase: phrase || ''
      });
    }
    if (lane.exclude) push('artist:' + lane.exclude, 95, 'artist');
    if (lane.seeds) {
      for (i = 0; i < lane.seeds.length; i++) {
        var s = lane.seeds[i];
        if (data && data.artists && data.artists[s]) {
          push('artist:' + s, 80, 'artist');
        }
      }
    }
    if (lane.g) {
      for (i = 0; i < lane.g.length; i++) {
        var gs = lane.g[i];
        if (data && data.genres && data.genres[gs]) {
          push('genre:' + gs, 70, 'genre');
        } else {
          // Genre slug may still expand via expand() even without a node.
          push('genre:' + gs, 65, 'genre');
        }
      }
    }
  }

  /**
   * Sync OOV "like X" lane: catalog artist → baked affinity → live cache.
   * Returns row or null when known-miss; undefined when not yet resolvable.
   */
  function resolveLikeLaneSync(phrase) {
    var q = normalize(phrase);
    if (!q || q.length < 2) return null;
    try {
      if (
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(q)
      ) {
        return null;
      }
    } catch (eGuard) {
      /* ignore */
    }
    var catalogHit = findCatalogArtist(q);
    if (catalogHit && catalogHit.score >= 50) {
      var catLane = laneFromCatalogArtist(catalogHit.slug, catalogHit.node);
      if (catLane) return catLane;
    }
    var affHit = lookupAffinity(q, affinityCache);
    if (affHit && affHit.row && affHit.row.g && affHit.row.g.length) {
      return {
        n: affHit.row.n || affHit.key,
        g: affHit.row.g.slice(),
        c: Array.isArray(affHit.row.c) ? affHit.row.c.slice() : [],
        seeds: Array.isArray(affHit.row.seeds) ? affHit.row.seeds.slice() : [],
        src: affHit.row.src || 'affinity'
      };
    }
    if (Object.prototype.hasOwnProperty.call(liveAffinityCache, q)) {
      return liveAffinityCache[q];
    }
    return undefined;
  }

  /**
   * Resolve "like X" neighbour lane (catalog → baked affinity → live MB).
   * Rock/pop primaries return null (no_lane) — caller keeps apology / genre-only.
   */
  function resolveLikeLane(phrase) {
    var q = normalize(phrase);
    if (!q || q.length < 2) return Promise.resolve(null);
    return ensureAffinity().then(function () {
      var sync = resolveLikeLaneSync(phrase);
      if (sync !== undefined) return sync;
      return fetchLiveAffinity(phrase);
    });
  }

  /**
   * Live MusicBrainz lane for OOV queries missing baked affinity.
   * Returns affinity-shaped row { n, g, c, mbid, src } or null.
   */
  function fetchLiveAffinity(query) {
    var q = normalize(query);
    if (!q || q.length < 2) return Promise.resolve(null);
    try {
      if (
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(q)
      ) {
        liveAffinityCache[q] = null;
        return Promise.resolve(null);
      }
    } catch (eGuard) {
      /* ignore */
    }
    if (Object.prototype.hasOwnProperty.call(liveAffinityCache, q)) {
      return Promise.resolve(liveAffinityCache[q]);
    }
    if (liveAffinityInflight[q]) return liveAffinityInflight[q];
    var base = prefsProxyBase();
    if (!base || typeof fetch !== 'function') {
      liveAffinityCache[q] = null;
      return Promise.resolve(null);
    }
    var url =
      base +
      '/mb-affinity?v=7&q=' +
      encodeURIComponent(String(query || '').trim());
    liveAffinityInflight[q] = fetch(url, { credentials: 'omit' })
      .then(function (res) {
        // 404 no_lane is a real answer — cache it. Other errors must retry.
        if (!res) throw new Error('mb-affinity empty');
        if (res.status === 404) {
          liveAffinityCache[q] = null;
          return null;
        }
        if (!res.ok) throw new Error('mb-affinity ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (json == null) return null;
        var row = null;
        if (
          json &&
          json.ok !== false &&
          Array.isArray(json.g) &&
          json.g.length
        ) {
          row = {
            n: json.n || query,
            g: json.g.slice(),
            c: Array.isArray(json.c) ? json.c.slice() : [],
            seeds: Array.isArray(json.seeds) ? json.seeds.slice() : [],
            mbid: json.mbid || '',
            src: json.src || 'musicbrainz-live'
          };
          // Live MB "eric" → Eric Clapton — same given-name stub rule as affinity.
          if (isWeakGivenNamePrefix(q, normalize(row.n || ''))) {
            row = null;
          }
        }
        liveAffinityCache[q] = row;
        return row;
      })
      .catch(function () {
        // Transient network/5xx — do not poison the session cache.
        return null;
      })
      .then(function (row) {
        delete liveAffinityInflight[q];
        return row;
      });
    return liveAffinityInflight[q];
  }

  function ensureAffinity() {
    if (affinityCache) return Promise.resolve(affinityCache);
    if (affinityPromise) return affinityPromise;
    var url = affinityUrl();
    if (!url || typeof fetch !== 'function') {
      affinityCache = { artists: {} };
      return Promise.resolve(affinityCache);
    }
    affinityPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('affinity ' + res.status);
        return res.json();
      })
      .then(function (json) {
        affinityCache = json && json.artists ? json : { artists: {} };
        affinityPromise = null;
        return affinityCache;
      })
      .catch(function () {
        affinityCache = { artists: {} };
        affinityPromise = null;
        return affinityCache;
      });
    return affinityPromise;
  }

  function affinityLcsRatio(a, b) {
    a = String(a || '');
    b = String(b || '');
    if (!a || !b) return 0;
    if (a === b) return 1;
    if (Math.abs(a.length - b.length) > 3) return 0;
    var m = a.length;
    var n = b.length;
    var prev = new Array(n + 1);
    var i;
    var j;
    for (j = 0; j <= n; j++) prev[j] = 0;
    for (i = 1; i <= m; i++) {
      var cur = [0];
      for (j = 1; j <= n; j++) {
        cur[j] =
          a.charAt(i - 1) === b.charAt(j - 1)
            ? prev[j - 1] + 1
            : Math.max(prev[j], cur[j - 1]);
      }
      prev = cur;
    }
    return prev[n] / Math.max(m, n);
  }

  function lookupAffinity(queryNorm, aff) {
    if (!aff || !aff.artists || !queryNorm) return null;
    if (aff.artists[queryNorm]) {
      return { key: queryNorm, row: aff.artists[queryNorm] };
    }
    // Prefix / contained key (e.g. "tinariwen live")
    var best = null;
    var bestLen = 0;
    var k;
    for (k in aff.artists) {
      if (!Object.prototype.hasOwnProperty.call(aff.artists, k)) continue;
      if (queryNorm === k || queryNorm.indexOf(k) === 0 || k.indexOf(queryNorm) === 0) {
        // Block given-name stub expansion: "eric" must not become "eric clapton".
        if (
          k.indexOf(queryNorm) === 0 &&
          queryNorm !== k &&
          isWeakGivenNamePrefix(queryNorm, k)
        ) {
          continue;
        }
        if (queryNorm.length < 4 && k.indexOf(queryNorm) === 0 && queryNorm !== k) {
          continue;
        }
        if (k.length > bestLen) {
          bestLen = k.length;
          best = { key: k, row: aff.artists[k] };
        }
      }
    }
    if (best) return best;
    // Typo tolerance: Tiniwaren → Tinariwen via LCS (edit distance alone is 4).
    if (queryNorm.length < 6) return null;
    var bestRatio = 0;
    for (k in aff.artists) {
      if (!Object.prototype.hasOwnProperty.call(aff.artists, k)) continue;
      if (k.length < 6) continue;
      var ratio = affinityLcsRatio(queryNorm, k);
      if (ratio >= 0.75 && ratio > bestRatio) {
        bestRatio = ratio;
        best = { key: k, row: aff.artists[k] };
      }
    }
    return best;
  }

  /**
   * Best in-catalog artist for a normalized query name.
   * @returns {{ slug: string, node: object, score: number }|null}
   */
  function findCatalogArtist(queryNorm) {
    if (!data || !data.artists || !queryNorm) return null;
    var best = null;
    var bestScore = 0;
    var slug;
    for (slug in data.artists) {
      if (!Object.prototype.hasOwnProperty.call(data.artists, slug)) continue;
      var node = data.artists[slug];
      var nameN = normalize(node.n);
      var slugN = normalize(slug.replace(/-/g, ' '));
      var s = Math.max(
        scoreNameMatch(queryNorm, nameN),
        scoreNameMatch(queryNorm, slugN)
      );
      // Charlie Patton → Charley Patton (catalog spelling).
      if (s < 50 && queryNorm.length >= 8) {
        var ratio = Math.max(
          affinityLcsRatio(queryNorm, nameN),
          affinityLcsRatio(queryNorm, slugN)
        );
        if (ratio >= 0.88) s = Math.round(ratio * 95);
      }
      if (s <= bestScore) continue;
      // Identity only: skip given-name stubs and mid-name token hits
      // ("john" inside "Bob Brozman, John McSherry…").
      if (
        isWeakGivenNamePrefix(queryNorm, nameN) ||
        isWeakGivenNamePrefix(queryNorm, slugN)
      ) {
        continue;
      }
      if (
        queryNorm.indexOf(' ') === -1 &&
        nameN !== queryNorm &&
        slugN !== queryNorm &&
        nameN.indexOf(queryNorm) !== 0 &&
        slugN.indexOf(queryNorm) !== 0
      ) {
        continue;
      }
      bestScore = s;
      best = { slug: slug, node: node, score: s };
    }
    return bestScore >= 50 ? best : null;
  }

  /** Countries appearing on an artist's products. */
  function countriesForArtist(slug) {
    var out = [];
    var seen = {};
    var node = data && data.artists && data.artists[slug];
    if (!node || !node.p) return out;
    var i;
    for (i = 0; i < node.p.length; i++) {
      var p = data.products[node.p[i]];
      if (!p || !p.c) continue;
      var j;
      for (j = 0; j < p.c.length; j++) {
        var c = p.c[j];
        if (c && c !== 'celtic' && !seen[c]) {
          seen[c] = true;
          out.push(c);
        }
      }
    }
    return out;
  }

  /**
   * Mean of an artist's product embeddings (catalog-native query vector).
   * Falls back to null when no baked embs exist.
   */
  function artistEmbeddingCentroid(slug) {
    var node = data && data.artists && data.artists[slug];
    if (!node || !node.p || !node.p.length) return null;
    var acc = null;
    var n = 0;
    var i;
    for (i = 0; i < node.p.length; i++) {
      var pe = getProductEmbedding(node.p[i]);
      if (!pe || !pe.length) continue;
      if (!acc) {
        acc = pe.slice();
      } else {
        var d;
        for (d = 0; d < pe.length; d++) acc[d] += pe[d];
      }
      n++;
    }
    if (!n || !acc) return null;
    var n2 = 0;
    for (i = 0; i < acc.length; i++) {
      acc[i] /= n;
      n2 += acc[i] * acc[i];
    }
    n2 = Math.sqrt(n2) || 1;
    for (i = 0; i < acc.length; i++) acc[i] /= n2;
    return acc;
  }

  /**
   * Live genre-neighbour artists when artists.*.r is sparse.
   * Prefer specific genres over world-music catch-alls.
   */
  function genreNeighborArtists(genres, excludeSlug, limit) {
    limit = limit != null ? limit : 8;
    var GENERIC = {
      'world-music': 1,
      'world-music-compilation': 1,
      'world-compilation': 1
    };
    var specific = [];
    var i;
    for (i = 0; i < (genres || []).length; i++) {
      if (!GENERIC[genres[i]]) specific.push(genres[i]);
    }
    var lane = specific.length ? specific : genres || [];
    if (!lane.length || !data.artists) return [];
    var scored = [];
    var slug;
    for (slug in data.artists) {
      if (!Object.prototype.hasOwnProperty.call(data.artists, slug)) continue;
      if (slug === excludeSlug) continue;
      var ag = data.artists[slug].g || [];
      var shared = 0;
      var gi;
      for (gi = 0; gi < lane.length; gi++) {
        if (ag.indexOf(lane[gi]) !== -1) shared++;
      }
      if (!shared) continue;
      scored.push({
        slug: slug,
        score: shared / lane.length + (data.artists[slug].r && data.artists[slug].r.length ? 0.05 : 0)
      });
    }
    scored.sort(function (a, b) {
      return b.score - a.score || (a.slug < b.slug ? -1 : 1);
    });
    var out = [];
    for (i = 0; i < scored.length && out.length < limit; i++) {
      out.push(scored[i].slug);
    }
    return out;
  }

  /**
   * Similar catalogue artists/releases for a query with no exact hits.
   *
   * Resolution order:
   *   1. Strong in-catalog artist (≥70) → artists.*.g / *.r + product-emb centroid
   *   2. OOV affinity JSON (MusicBrainz-baked)
   *   3. Weaker catalog artist (≥50)
   *   4. Pure embedding neighbours
   *
   * @returns {{
   *   similarTo: string,
   *   handles: string[],
   *   scores: Object.<string,number>,
   *   reason: string,
   *   genres: string[]
   * }|null}
   */
  function similarToQuery(text, opts) {
    opts = opts || {};
    if (!data || !data.products) return null;
    var q = normalize(text);
    if (!q || q.length < 2) return null;
    var limit = opts.limit != null ? opts.limit : 24;
    var aff = affinityCache;
    var hit = lookupAffinity(q, aff);
    // Injected live MusicBrainz row (after baked miss).
    if (!hit && opts.liveRow && opts.liveRow.g && opts.liveRow.g.length) {
      hit = { key: q, row: opts.liveRow };
    }
    var catalogHit = findCatalogArtist(q);

    var genres = [];
    var countries = [];
    var seedArtists = [];
    var excludeArtist = null;
    var similarTo = text;
    var reason = 'embed';
    var qEmb = null;
    var fromCatalog = false;

    // Prefer catalog graph when the artist is actually in our catalogue.
    if (catalogHit && catalogHit.score >= 70) {
      fromCatalog = true;
      reason = 'catalog-artist';
      excludeArtist = catalogHit.slug;
      similarTo = catalogHit.node.n || catalogHit.slug;
      genres = (catalogHit.node.g || []).slice();
      countries = countriesForArtist(catalogHit.slug);
      seedArtists = (catalogHit.node.r || []).slice();
      if (seedArtists.length < 4) {
        var live = genreNeighborArtists(genres, catalogHit.slug, 8);
        var li;
        for (li = 0; li < live.length; li++) {
          if (seedArtists.indexOf(live[li]) === -1) seedArtists.push(live[li]);
        }
      }
      if (seedArtists.length > 8) seedArtists = seedArtists.slice(0, 8);
      qEmb = artistEmbeddingCentroid(catalogHit.slug);
    } else if (hit && hit.row) {
      reason =
        hit.row.src === 'musicbrainz-live' ? 'musicbrainz-live' : 'affinity';
      similarTo = hit.row.n || hit.key;
      genres = (hit.row.g || []).slice();
      countries = (hit.row.c || []).slice();
      seedArtists = (hit.row.seeds || []).slice();
    } else if (catalogHit) {
      fromCatalog = true;
      reason = 'catalog-artist';
      excludeArtist = catalogHit.slug;
      similarTo = catalogHit.node.n || catalogHit.slug;
      genres = (catalogHit.node.g || []).slice();
      countries = countriesForArtist(catalogHit.slug);
      seedArtists = (catalogHit.node.r || []).slice();
      if (seedArtists.length < 4) {
        var liveWeak = genreNeighborArtists(genres, catalogHit.slug, 8);
        var lwi;
        for (lwi = 0; lwi < liveWeak.length; lwi++) {
          if (seedArtists.indexOf(liveWeak[lwi]) === -1) {
            seedArtists.push(liveWeak[lwi]);
          }
        }
      }
      if (seedArtists.length > 8) seedArtists = seedArtists.slice(0, 8);
      qEmb = artistEmbeddingCentroid(catalogHit.slug);
    }

    // No catalog artist and no affinity lane → refuse random emb neighbours
    // (Guns N' Roses → Steve Tilston / Blues Divas). UI shows the empty message.
    if (!fromCatalog && !hit) {
      return null;
    }

    // Prefer specific genres for lane filtering.
    var GENERIC_G = {
      'world-music': 1,
      'world-music-compilation': 1,
      'world-compilation': 1
    };
    var i;
    var laneGenres = [];
    for (i = 0; i < genres.length; i++) {
      if (!GENERIC_G[genres[i]]) laneGenres.push(genres[i]);
    }
    // Jazz Legends: drop blues co-tag. Sahara/Tuareg: drop generic blues —
    // desert-blues→blues relatedness otherwise dumps delta / Charley Patton.
    if (laneGenres.indexOf('jazz') !== -1 && laneGenres.indexOf('blues') !== -1) {
      laneGenres = laneGenres.filter(function (g) {
        return g !== 'blues';
      });
    }
    if (
      (laneGenres.indexOf('tuareg') !== -1 ||
        laneGenres.indexOf('desert-blues-sahara') !== -1) &&
      laneGenres.indexOf('blues') !== -1
    ) {
      laneGenres = laneGenres.filter(function (g) {
        return g !== 'blues';
      });
    }
    if (!laneGenres.length) laneGenres = genres.slice();

    // Affinity + catalog: seed artists + lane-genre products only (depth 0).
    // Never walk genre relatedness (desert-blues → blues → whole US catalog).
    var expandIds = [];
    var tightLane = fromCatalog || !!hit;
    for (i = 0; i < seedArtists.length; i++) {
      expandIds.push('artist:' + seedArtists[i]);
    }
    if (tightLane) {
      for (i = 0; i < laneGenres.length; i++) {
        expandIds.push('genre:' + laneGenres[i]);
      }
    } else {
      for (i = 0; i < laneGenres.length; i++) expandIds.push('genre:' + laneGenres[i]);
      for (i = 0; i < countries.length; i++) {
        if (countries[i] && countries[i] !== 'celtic') {
          expandIds.push('country:' + countries[i]);
        }
      }
    }

    var handles = {};
    if (expandIds.length) {
      var expanded = expand(expandIds, 0) || [];
      for (i = 0; i < expanded.length; i++) handles[expanded[i]] = true;
    }

    // Query vector: artist product-emb centroid when catalog-native, else text hash.
    if (!qEmb) {
      var embQuery = q;
      if (laneGenres.length || seedArtists.length) {
        embQuery =
          q +
          ' ' +
          laneGenres.join(' ').replace(/-/g, ' ') +
          ' ' +
          seedArtists.join(' ').replace(/-/g, ' ');
      }
      qEmb = embedText(embQuery);
    }

    var scores = {};
    var h;
    var minEmb = fromCatalog ? 0.08 : hit ? 0.1 : 0.18;
    var ownProducts = {};
    if (excludeArtist && data.artists[excludeArtist] && data.artists[excludeArtist].p) {
      var op = data.artists[excludeArtist].p;
      for (i = 0; i < op.length; i++) ownProducts[op[i]] = true;
    }

    for (h in data.products) {
      if (!Object.prototype.hasOwnProperty.call(data.products, h)) continue;
      // Similar-to should surface neighbours, not the matched artist's own titles.
      if (ownProducts[h]) continue;
      var pe = getProductEmbedding(h);
      var emb = pe ? dotEmbedding(qEmb, pe) : 0;
      var bonus = 0;
      var p = data.products[h];
      var isSeed = seedArtists.length && p && p.a && seedArtists.indexOf(p.a) !== -1;
      if (isSeed) bonus += 0.5;
      var genreHit = false;
      if (laneGenres.length && p && p.g) {
        var gi;
        for (gi = 0; gi < p.g.length; gi++) {
          if (laneGenres.indexOf(p.g[gi]) !== -1) {
            bonus += 0.3;
            genreHit = true;
            break;
          }
        }
      }
      var countryHit = false;
      if (countries.length && p && p.c) {
        for (gi = 0; gi < p.c.length; gi++) {
          if (countries.indexOf(p.c[gi]) !== -1) {
            bonus += 0.12;
            countryHit = true;
            break;
          }
        }
      }
      // Known lane (affinity / catalog): seed OR shared specific genre.
      // Country alone is not enough (Rough Guide Mali comps without desert blues).
      if (tightLane && !isSeed && !genreHit) continue;
      // Multi-genre Rough Guide comps are tagged tuareg/desert-blues among
      // 5–10 other genres — treat as compilation noise unless seed artist.
      var genreCount = (p.g || []).length;
      if (!isSeed && genreCount >= 4) continue;
      if (!isSeed && !p.a && genreCount >= 3) continue;
      // Pure embed: keep the bar high — no random Hlybini / Eva Quartet.
      if (!tightLane && emb < minEmb) continue;
      var total = emb + bonus;
      if (isSeed || genreHit || handles[h] || total >= minEmb) {
        if (!isSeed && p.g && p.g.length === 1 && GENERIC_G[p.g[0]]) {
          total *= 0.4;
        }
        scores[h] = Math.round(total * 1000) / 1000;
        handles[h] = true;
      }
    }

    var ranked = Object.keys(handles);
    if (!ranked.length) return null;
    ranked.sort(function (a, b) {
      return (scores[b] || 0) - (scores[a] || 0) || (a < b ? -1 : 1);
    });
    if (tightLane) {
      ranked = ranked.filter(function (hh) {
        return (scores[hh] || 0) >= 0.28;
      });
    } else if (reason === 'embed') {
      ranked = ranked.filter(function (hh) {
        return (scores[hh] || 0) >= 0.22;
      });
    }
    if (!ranked.length) return null;
    if (ranked.length > limit) ranked = ranked.slice(0, limit);

    return {
      similarTo: similarTo,
      handles: ranked,
      scores: scores,
      reason: reason,
      genres: genres,
      countries: countries
    };
  }

  /**
   * Human label for a graph entity slug (genre / country / region / artist).
   * Used by discover “why” chrome: “Similar to Tinariwen · Desert blues · Mali”.
   */
  function entityDisplayName(kind, slug) {
    if (!slug) return '';
    var key = String(slug);
    var node = null;
    if (data) {
      if (kind === 'genre' && data.genres) node = data.genres[key];
      else if (kind === 'country' && data.countries) node = data.countries[key];
      else if (kind === 'region' && data.regions) node = data.regions[key];
      else if (kind === 'artist' && data.artists) node = data.artists[key];
    }
    if (node && node.n) return String(node.n);
    return key
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  /**
   * Decode products[h].e (base64 uint8) → float32-ish centered unit vector.
   * Returns null if missing / bad.
   */
  function dequantEmbedding(b64) {
    if (!b64 || typeof b64 !== 'string') return null;
    var meta = (data && data.emb) || {};
    var dim = meta.d || 64;
    var zero = meta.zero != null ? meta.zero : 128;
    var scale = meta.scale != null ? meta.scale : 127;
    var raw;
    try {
      if (typeof atob !== 'function') return null;
      var bin = atob(b64);
      if (bin.length < dim) return null;
      raw = bin;
    } catch (e) {
      return null;
    }
    var vec = new Array(dim);
    var i;
    var n2 = 0;
    for (i = 0; i < dim; i++) {
      var x = (raw.charCodeAt(i) - zero) / scale;
      vec[i] = x;
      n2 += x * x;
    }
    n2 = Math.sqrt(n2) || 1;
    for (i = 0; i < dim; i++) vec[i] /= n2;
    return vec;
  }

  function getProductEmbedding(handle) {
    if (!handle || !data || !data.products) return null;
    var p = data.products[handle];
    if (!p || !p.e) return null;
    return dequantEmbedding(p.e);
  }

  /** Spotify embed path if PDP has embed; else ''. Never invents URIs. */
  function getSpotifyPath(handle) {
    if (!handle || !data || !data.products) return '';
    var p = data.products[handle];
    var sp = p && p.sp ? String(p.sp) : '';
    if (!/^(album|track|playlist|episode)\/[A-Za-z0-9]+$/.test(sp)) return '';
    return sp;
  }

  function dotEmbedding(a, b) {
    if (!a || !b || a.length !== b.length) return 0;
    var s = 0;
    var i;
    for (i = 0; i < a.length; i++) s += a[i] * b[i];
    return s;
  }

  window.wmnCatalogGraph = {
    ensureLoaded: ensureLoaded,
    ensureAffinity: ensureAffinity,
    fetchLiveAffinity: fetchLiveAffinity,
    resolveLikeLane: resolveLikeLane,
    resolveLikeLaneSync: resolveLikeLaneSync,
    syncLiveProducts: syncLiveProducts,
    resolveProduct: resolveProduct,
    expand: expand,
    relatedGenres: relatedGenres,
    linkQuery: linkQuery,
    exactGenreFromQuery: exactGenreFromQuery,
    parseSearchIntent: parseSearchIntent,
    graphSearch: graphSearch,
    similarToQuery: similarToQuery,
    entityDisplayName: entityDisplayName,
    scoreHandleForIntent: scoreHandleForIntent,
    productCard: productCard,
    embedText: embedText,
    getProductImage: getProductImage,
    hasImageCacheEntry: hasImageCacheEntry,
    hydrateImages: hydrateImages,
    getProductEmbedding: getProductEmbedding,
    getSpotifyPath: getSpotifyPath,
    dequantEmbedding: dequantEmbedding,
    dotEmbedding: dotEmbedding,
    getData: function () {
      return data;
    }
  };
})(window, document);
